import { useState } from "react";
import useSWR, { SWRConfig, useSWRConfig } from "swr";
import { DownloadIcon, PencilIcon, PlusIcon, UploadIcon } from "lucide-react";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { RefreshButton } from "@/components/list/refresh-button";
import { Chip } from "@/components/badge/chip";
import { TabBar } from "@/components/nav/route-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ExecTemplateEditor,
  type ExecTemplate,
} from "@/components/prds/exec-template-editor";
import {
  PublishTemplateDialog,
  PUBLICATIONS_PATH,
  TEMPLATE_CONFIG_PATH,
  MY_TEMPLATES_PATH,
  type ExecPublication,
  type PublicationList,
  type TemplateSharingConfig,
} from "@/components/prds/exec-template-publication";
import { useUserMap } from "@/hooks/users";
import { useToast } from "@/hooks/use-toast";
import { delFetcher, postFetcher, useAuthStore, swrKey } from "@/lib/swr";
import { toastError } from "@/lib/toast";

const PAGE_SIZE = 20;
const EMPTY_VARS = {};

const TemplatePreview = ({
  publication,
  onOpenChange,
}: {
  publication: ExecPublication;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data, error, mutate } = useSWR<ExecPublication>([
    `${PUBLICATIONS_PATH}/${publication.id}`,
  ]);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{publication.name}</DialogTitle>
          <DialogDescription>
            安装后可自行编辑，变量会在使用时按当前需求替换。
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div role="alert" className="space-y-2">
            <p className="text-sm">模板已撤回、不可见或加载失败</p>
            <Button variant="outline" size="sm" onClick={() => void mutate()}>
              重试
            </Button>
          </div>
        ) : !data ? (
          <ListLoading>加载模板…</ListLoading>
        ) : (
          <Textarea
            aria-label="模板正文"
            readOnly
            value={data.body ?? ""}
            className="max-h-[65vh] min-h-48 font-mono text-xs"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ExecTemplatesContent = () => {
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const people = useUserMap();
  const [tab, setTab] = useState("browse");
  const [q, setQ] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [editing, setEditing] = useState<{ template?: ExecTemplate } | null>(
    null,
  );
  const [publishing, setPublishing] = useState<ExecTemplate | null>(null);
  const [preview, setPreview] = useState<ExecPublication | null>(null);
  const [withdrawing, setWithdrawing] = useState<ExecPublication | null>(null);
  const [installing, setInstalling] = useState<string | null>(null);
  const {
    data: config,
    error: configError,
    mutate: reloadConfig,
  } = useSWR<TemplateSharingConfig>([TEMPLATE_CONFIG_PATH]);
  const {
    data: own,
    error: ownError,
    mutate: reloadOwn,
    isValidating: loadingOwn,
  } = useSWR<ExecTemplate[]>([MY_TEMPLATES_PATH]);
  const {
    data: published,
    error: publishedError,
    mutate: reloadPublished,
    isValidating: loadingPublished,
  } = useSWR<PublicationList>(
    config?.enabled && tab !== "mine"
      ? swrKey(PUBLICATIONS_PATH, {
          q,
          mine: tab === "published" ? "true" : "false",
          pageIndex,
          pageSize: PAGE_SIZE,
        })
      : null,
    // Browse tab / search / page switches keep the old rows until the new ones land.
    { keepPreviousData: true },
  );
  const refresh = () => {
    setPageIndex(0);
    void reloadConfig();
    void reloadOwn();
    void reloadPublished();
  };
  const refreshPublications = () => {
    void mutate(
      (key) =>
        Array.isArray(key) &&
        (key[0] === PUBLICATIONS_PATH ||
          (typeof key[0] === "string" &&
            key[0].startsWith(`${PUBLICATIONS_PATH}/`) &&
            key[0] !== TEMPLATE_CONFIG_PATH) ||
          (typeof key[0] === "string" &&
            key[0].startsWith(`${MY_TEMPLATES_PATH}/`) &&
            key[0].endsWith("/publication"))),
      undefined,
      { revalidate: true },
    );
  };
  const install = async (p: ExecPublication) => {
    if (installing) return;
    setInstalling(p.id);
    try {
      await postFetcher<ExecTemplate>(`${PUBLICATIONS_PATH}/${p.id}/install`, {
        arg: {},
      });
      toast({
        title: "已加入我的模板",
        description: "可在 Open agent 的模板下拉中选用",
      });
      void reloadOwn();
      refreshPublications();
    } catch (e) {
      toastError(toast, "安装模板失败", e);
      refreshPublications();
    } finally {
      setInstalling(null);
    }
  };
  const withdraw = async () => {
    if (!withdrawing) return;
    try {
      await delFetcher(PUBLICATIONS_PATH, { arg: withdrawing.id });
      toast({
        title: "已撤回发布",
        description: "其他员工已安装的副本不受影响",
      });
      setPageIndex(0);
      refreshPublications();
    } catch (e) {
      toastError(toast, "撤回发布失败", e);
    }
    setWithdrawing(null);
  };
  const mine = tab === "mine";
  const visibleOwn = (own ?? []).filter((t) =>
    t.name.toLocaleLowerCase().includes(q.trim().toLocaleLowerCase()),
  );
  const error = mine ? ownError : publishedError;
  const rows = published?.data ?? [];

  return (
    <div className="space-y-4">
      <PageBreadcrumb current="Prompt 模板" />
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Prompt 模板</h1>
        <p className="text-sm text-muted-foreground">
          浏览同事发布的模板，安装后加入自己的模板下拉，也可以修改后再次发布。
        </p>
      </header>
      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTab(next);
          setQ("");
          setPageIndex(0);
        }}
      >
        <TabBar
          action={
            <div className="flex gap-2">
              {mine && (
                <Button
                  size="sm"
                  disabled={!config?.variables.length}
                  onClick={() => setEditing({})}
                >
                  <PlusIcon />
                  新建模板
                </Button>
              )}
              <RefreshButton
                spinning={mine ? loadingOwn : loadingPublished}
                onClick={refresh}
              />
            </div>
          }
        >
          <TabsTrigger value="browse">浏览模板</TabsTrigger>
          <TabsTrigger value="mine">我的模板</TabsTrigger>
          <TabsTrigger value="published">我发布的</TabsTrigger>
        </TabBar>
      </Tabs>
      <Input
        aria-label="搜索模板"
        placeholder="搜索模板名称"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setPageIndex(0);
        }}
        maxLength={200}
        className="sm:max-w-sm"
      />
      {configError && (
        <p role="alert" className="text-sm text-destructive">
          加载模板配置失败，请刷新重试
        </p>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          加载模板失败，请刷新重试
        </p>
      ) : mine ? (
        !own ? (
          <ListLoading>加载我的模板…</ListLoading>
        ) : !visibleOwn.length ? (
          <ListEmpty>
            {q
              ? "没有找到匹配的模板"
              : "还没有个人模板，可以新建或从浏览页安装"}
          </ListEmpty>
        ) : (
          <div className="flex flex-col gap-1">
            {visibleOwn.map((t, i) => (
              <RowCard key={t.id}>
                <RowIndex n={i + 1} />
                <div className="min-w-0 flex-1 truncate">{t.name}</div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!config?.variables.length}
                    onClick={() => setEditing({ template: t })}
                  >
                    <PencilIcon />
                    编辑
                  </Button>
                  {config?.enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPublishing(t)}
                    >
                      <UploadIcon />
                      发布
                    </Button>
                  )}
                </div>
              </RowCard>
            ))}
          </div>
        )
      ) : !config ? (
        configError ? null : (
          <ListLoading>加载模板配置…</ListLoading>
        )
      ) : !config.enabled ? (
        <ListEmpty>模板发布与安装暂未开放，已有个人模板仍可使用。</ListEmpty>
      ) : !published ? (
        <ListLoading>加载发布的模板…</ListLoading>
      ) : !rows.length ? (
        <ListEmpty>
          {q
            ? "没有找到匹配的模板"
            : tab === "published"
            ? "还没有发布模板，可从我的模板中发布"
            : "暂无可安装的模板"}
        </ListEmpty>
      ) : (
        <div className="flex flex-col gap-1">
          {rows.map((p, i) => {
            const source = own?.find((t) => t.id === p.sourceTemplateId);
            return (
              <RowCard
                key={p.id}
                onClick={() => setPreview(p)}
                className="flex-wrap sm:flex-nowrap"
              >
                <RowIndex n={pageIndex * PAGE_SIZE + i + 1} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{p.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {people.get(p.publisherId)?.name ?? p.publisherId}
                  </div>
                </div>
                <Chip>{p.audience === "all" ? "全员" : "指定员工"}</Chip>
                <div className="ml-auto flex w-full shrink-0 flex-wrap justify-end gap-1 sm:w-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreview(p)}
                  >
                    预览
                  </Button>
                  {tab === "published" ? (
                    <>
                      {source && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPublishing(source)}
                        >
                          再次发布
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setWithdrawing(p)}
                      >
                        撤回
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      disabled={!!installing || !!p.installedTemplateId}
                      onClick={() => void install(p)}
                    >
                      <DownloadIcon />
                      {p.installedTemplateId
                        ? "已安装"
                        : installing === p.id
                        ? "安装中…"
                        : "安装"}
                    </Button>
                  )}
                </div>
              </RowCard>
            );
          })}
        </div>
      )}
      {!mine && config?.enabled && published && published.total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={pageIndex === 0 || loadingPublished}
            onClick={() => setPageIndex(pageIndex - 1)}
          >
            上一页
          </Button>
          <span>
            {pageIndex + 1} / {Math.ceil(published.total / PAGE_SIZE)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={
              (pageIndex + 1) * PAGE_SIZE >= published.total || loadingPublished
            }
            onClick={() => setPageIndex(pageIndex + 1)}
          >
            下一页
          </Button>
        </div>
      )}
      {editing && config && (
        <ExecTemplateEditor
          key={editing.template?.id ?? "new"}
          open
          template={editing.template}
          variables={config.variables}
          vars={EMPTY_VARS}
          previewLabel="预览（变量在使用时替换）"
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={() => {
            void reloadOwn();
          }}
          onDeleted={() => {
            void reloadOwn();
            refreshPublications();
          }}
        />
      )}
      {publishing && (
        <PublishTemplateDialog
          template={publishing}
          onOpenChange={(open) => !open && setPublishing(null)}
          onSaved={refreshPublications}
        />
      )}
      {preview && (
        <TemplatePreview
          publication={preview}
          onOpenChange={(open) => !open && setPreview(null)}
        />
      )}
      <AlertDialog
        open={!!withdrawing}
        onOpenChange={(open) => !open && setWithdrawing(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>撤回「{withdrawing?.name}」？</AlertDialogTitle>
            <AlertDialogDescription>
              撤回后其他员工无法继续浏览或安装，已经安装的个人副本不受影响。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={() => void withdraw()}>
              撤回发布
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// 全站 SWR 会保留一分钟缓存;按登录会话隔离私人模板和指定范围的发布内容。
const ExecTemplates = () => {
  const session = useAuthStore((state) => state.auth.id);
  return (
    <SWRConfig key={session} value={{ provider: () => new Map() }}>
      <ExecTemplatesContent />
    </SWRConfig>
  );
};

export default ExecTemplates;
